import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Stack,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { renderPdfFirstPageToWebp } from "../../utils/pdfToWebp";

export type ImageCropperProps = {
  open: boolean;
  file: File | Blob | null;
  onClose: () => void;
  onSave: (processedBlob: Blob) => void;
};

export const ImageCropper = ({
  open,
  file,
  onClose,
  onSave,
}: ImageCropperProps) => {
  const [prevFile, setPrevFile] = useState<File | Blob | null>(null);
  const [rasterSrc, setRasterSrc] = useState<string | null>(null);
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [originalWidth, setOriginalWidth] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);

  // Editor state
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const lastPinchDistance = useRef<number | null>(null);

  if (file !== prevFile) {
    setPrevFile(file);
    setRasterSrc(null);
    setImageEl(null);
    const isPdf = file?.type === "application/pdf";
    setIsProcessing(isPdf);
    setDebugLogs(isPdf ? ["Started processing PDF"] : []);
  }

  useEffect(() => {
    if (!file) {
      return;
    }

    let isMounted = true;
    let currentUrl: string | null = null;

    const loadImage = (url: string) => {
      const img = new Image();
      img.onload = () => {
        if (!isMounted) return;
        const w = img.width;
        const h = img.height;
        const ratio = w / h || 1;
        setAspectRatio(ratio);
        setOriginalWidth(w);

        setRasterSrc(url);
        setImageEl(img);

        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setIsProcessing(false);
      };
      img.src = url;
    };

    if (file.type === "application/pdf") {
      renderPdfFirstPageToWebp(file, {
        onLog: (msg) => setDebugLogs((prev) => [...prev, msg]),
      })
        .then((blob) => {
          if (!isMounted) return;
          currentUrl = URL.createObjectURL(blob);
          loadImage(currentUrl);
        })
        .catch((e) => {
          const errorMessage = e instanceof Error ? e.message : String(e);
          setDebugLogs((prev) => [...prev, `Error: ${errorMessage}`]);
          setIsProcessing(false);
        });
    } else {
      currentUrl = URL.createObjectURL(file);
      loadImage(currentUrl);
    }

    return () => {
      isMounted = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [file]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - lastMousePos.current.x;
    const dy = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const getDistance = (t1: React.Touch, t2: React.Touch) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastMousePos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    } else if (e.touches.length === 2) {
      setIsDragging(false);
      const dist = getDistance(e.touches[0], e.touches[1]);
      lastPinchDistance.current = dist;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - lastMousePos.current.x;
      const dy = e.touches[0].clientY - lastMousePos.current.y;
      lastMousePos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    } else if (e.touches.length === 2 && lastPinchDistance.current !== null) {
      const dist = getDistance(e.touches[0], e.touches[1]);
      const zoomFactor = dist / lastPinchDistance.current;
      setZoom((prevZoom) => Math.min(Math.max(prevZoom * zoomFactor, 0.1), 5));
      lastPinchDistance.current = dist;
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    lastPinchDistance.current = null;
  };

  const previewBaseSize = useMemo(() => {
    return { w: 256, h: 256 / aspectRatio };
  }, [aspectRatio]);

  const handleSave = () => {
    if (rasterSrc && imageEl) {
      const TARGET_WIDTH = Math.min(1920, originalWidth);
      const TARGET_HEIGHT = TARGET_WIDTH / aspectRatio;

      const canvas = document.createElement("canvas");
      // Compute the scale factor to go from the 256x256 view to TARGET_WIDTH
      const scaleFactor = TARGET_WIDTH / 256;

      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_HEIGHT;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const finalW = previewBaseSize.w * zoom * scaleFactor;
      const finalH = previewBaseSize.h * zoom * scaleFactor;
      const finalX = position.x * scaleFactor;
      const finalY = position.y * scaleFactor;

      ctx.drawImage(imageEl, finalX, finalY, finalW, finalH);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            onSave(blob);
          }
        },
        "image/webp",
        0.9,
      );
    }
  };

  const hasContent = !!rasterSrc;

  // Render a rectangular box that matches the aspect ratio of the target crop
  const previewBoxHeight = 256 / aspectRatio;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {file?.type === "application/pdf"
          ? "Crop PDF Preview"
          : "Crop Attachment Image"}
      </DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{
            alignItems: "center",
            mt: 1,
            userSelect: "none",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Drag to pan, use slider to zoom. Image will be cropped to the
            displayed area.
          </Typography>

          {/* Editor Container */}
          <Box
            sx={{
              width: 256,
              height: previewBoxHeight,
              position: "relative",
              overflow: "hidden",
              bgcolor: "#f0f0f0",
              cursor: isDragging ? "grabbing" : "grab",
              border: "1px solid #ccc",
              touchAction: "none",
            }}
            data-testid="image-cropper-canvas"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Checkboard pattern for transparency */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: "radial-gradient(#ccc 1px, transparent 1px)",
                backgroundSize: "10px 10px",
                opacity: 0.5,
                zIndex: 0,
              }}
            />

            {/* Loading Overlay */}
            {isProcessing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  zIndex: 2,
                  p: 2,
                }}
              >
                <CircularProgress sx={{ mb: 2 }} />
                {debugLogs.length > 0 && (
                  <Box
                    sx={{
                      width: "100%",
                      maxHeight: "60%",
                      overflowY: "auto",
                      textAlign: "left",
                      bgcolor: "black",
                      color: "limegreen",
                      p: 1,
                      borderRadius: 1,
                      fontSize: "0.7rem",
                      fontFamily: "monospace",
                    }}
                  >
                    {debugLogs.map((log, i) => (
                      <div key={i}>{log}</div>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Content Raster */}
            {rasterSrc && (
              <Box
                component="img"
                src={rasterSrc}
                alt="preview"
                sx={{
                  position: "absolute",
                  left: position.x,
                  top: position.y,
                  width: previewBaseSize.w * zoom,
                  height: previewBaseSize.h * zoom,
                  transformOrigin: "top left",
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Rectangular Overlay / Guide */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                border: "2px solid rgba(0,0,0,0.3)",
                pointerEvents: "none",
              }}
            />
          </Box>

          {/* Controls */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              width: "100%",
              px: 4,
            }}
          >
            <IconButton
              onClick={() => setZoom((z) => Math.max(0.1, z - 0.1))}
              aria-label="Zoom Out"
            >
              <ZoomOutIcon color="action" />
            </IconButton>
            <Slider
              value={zoom}
              min={0.1}
              max={5}
              step={0.1}
              onChange={(_, v) => setZoom(v as number)}
              aria-label="Zoom"
            />
            <IconButton
              onClick={() => setZoom((z) => Math.min(5, z + 0.1))}
              aria-label="Zoom In"
            >
              <ZoomInIcon color="action" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasContent}>
          Crop & Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
